<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2>Vendite</h2>
        <v-btn color="primary" size="small" @click="$router.push('/wizard')">Nuova</v-btn>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-autocomplete  v-model="query.user" label="Utente" clearable :items="userOptions" autocomplete="off"></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <!--<v-autocomplete  v-model="kitSearch" label="Pacchetto" clearable :items="kitOptions" ></v-autocomplete>-->
            <v-autocomplete  v-model="kitSearch" label="Pacchetto" clearable :items="kitOptions" autocomplete="off" ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-autocomplete  v-model="query.product" :disabled="!kitSearch" label="Prodotto" clearable
                             :items="productOptions" autocomplete="off" ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-text-field  v-model="query.sku" label="SKU Prodotto" clearable autocomplete="off"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-text-field  type="date" v-model="query.date_from" label="Venduti dal" clearable autocomplete="off"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-text-field  type="date" v-model="query.date_to" label="Venduti fino al" clearable autocomplete="off"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <v-switch  v-model="showDraft" label="Includi ordini aperti" @update:model-value="query.nodraft = showDraft ? 'false' : 'true'"></v-switch>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <data-table :headers="headers" collection="carts" view-sub-path="/view" :viewable="true" :editable="true" deletable :query="query" base-path="sells" @error="onListError" :default-sort="{key:'last_update', order : 'desc'}">
          <template v-slot:item.user.surname="{ item }">
            <a @click="$router.push(`/sells/${item._id}`)" ><strong>{{ item.user.surname }}</strong> {{ item.user.name }}</a>
          </template>
          <template v-slot:item.items="{ item }">
            <v-tooltip v-if="item.items" bottom>
              <template #activator="{ props }">
                <a class="text-decoration-underline cursor-pointer text-primary" v-bind="props"  >{{item.items.length}} prodott{{item.items.length === 1 ? 'o' : 'i'}}</a>
              </template>
              <ul class="pl-3">
                <li v-for="p in item.items" :key="`${item._id}_${p._id}`">{{p.title.it}}</li>
              </ul>
            </v-tooltip>

            <span v-else>0 prodotti</span>
          </template>
          <template v-slot:item.last_update="{ item }">
            {{formatDateTime(item.last_update)}}
          </template>
          <template v-slot:item.total="{ item }">
            € {{ getTotal(item) }}
          </template>
          <template v-slot:item.status="{ item }">
            <span v-if="item.status === 'draft'">Ordine aperto</span>
            <span v-if="item.status === 'paid'">Ordine pagato (in attesa di spedizione)</span>
            <span v-if="item.status === 'closed'">Ordine chiuso</span>
          </template>
          <template v-slot:item.expedition="{ item }">
            <span v-if="item.expedition">Sì</span>
            <span v-else>No</span>
          </template>
          <template v-slot:item.card="{ item }">
            <span v-if="item.card">Sì</span>
            <span v-else>No</span>
          </template>
          <template v-slot:item.home_kit="{ item }">
            <span v-if="item.home_kit">Sì</span>
            <span v-else>No</span>
          </template>
          <template v-slot:item.evaded="{ item }">
            <span v-if="item.evaded" class="text-success font-weight-bold">Sì</span>
            <span v-else class="text-error font-weight-bold">No</span>
          </template>

        </data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import {defineComponent, ref, computed, watch, onMounted, inject, onBeforeUnmount, useTemplateRef} from 'vue'
import { useRouter } from 'vue-router'
import utils from '../utils'
import DataTable from "@/components/DataTable.vue";
import {type CartDisplay, type DataTableError, type Query} from "@/models.ts";
import {type Messages, Msg} from "@/plugins/messages.ts";
import type {Auth} from "@/plugins/auth.ts";
import type {Globals} from "@/plugins/globals.ts";


export default defineComponent({
  components: {DataTable},
  setup() {
    const auth = inject('auth') as Auth
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const router = useRouter()
    const dataTable = useTemplateRef<typeof DataTable>('dataTable')
    const query = ref<Query>({
      nodraft : true
    })
    const kitSearch = ref<string|undefined>()
    const showDraft = ref<boolean>(false)

    let wh:any;
    onMounted(async ()=>{
      await auth.waitForLogin
      await globals.waitForInit

      await Promise.all([
        globals.refreshEmpty('users'),
        globals.refreshEmpty('kits'),
        globals.refreshEmpty('products'),
      ])
    })
    onBeforeUnmount(()=>{
      if(wh) wh();
    })
    return {
      dataTable,
      formatDateTime : utils.formatDateTime,
      query,
      kitSearch,
      showDraft,
      headers : [
        { title : 'Utente', sortable: true, align: 'left', value : 'user.surname' },
        { title : 'Ultimo aggiornamento', sortable: true, align: 'left', value : 'last_update' },
        { title : 'N° prodotti', sortable: false, align: 'left', value : 'items' },
        { title : 'Prezzo Totale', sortable: false, align: 'left', value : 'total' },
        { title : 'Stato', sortable: false, align: 'left', value : 'status' },
        { title : 'Spedizione', sortable: true, align: 'left', value : 'expedition' },
        { title : 'Tessera MSP', sortable: true, align: 'left', value : 'card' },
        { title : 'HomeKit', sortable: true, align: 'left', value : 'home_kit' },
        { title : 'Evaso', sortable: true, align: 'left', value : 'evaded' },
      ],
      userOptions : computed(()=>{
        return globals.users.value.map(i => {
          return { title : `${i.name} ${i.surname}`, value : i._id }
        })
      }),

      kitOptions : computed(()=>{
        let items= globals.kits.value.filter(i => i.pub)

        items.sort((a, b) => {
          if(a.category.is_gym_category && !b.category.is_gym_category) return -1
          if(!a.category.is_gym_category && b.category.is_gym_category) return 1
          if(a.order < b.order) return -1
          if(a.order > b.order) return 1
          return 0
        })
        return items.map(i => {
          return { title : i.title.it + ` (${i.order})`, value : i._id}
        })
      }),
      productOptions : computed(()=>{
        return globals.products.value.map(i => {
          return { title : i.title.it, value : i._id }
        })
      }),
      getTotal(item:CartDisplay) {
        let total = 0;
        for(let i = 0; i < item.items.length; i++) {
          total += item.items[i].price;
        }

        if(item.card) {
          total += globals.settings.value.coni_card_price
        }
        if(item.sub_pause_days) {
          total += item.sub_pause_days * globals.settings.value.extra_day_of_sub_pause_price
        }

        return total
      },
      formatDate : utils.formatDate,
      onListError(e:DataTableError) {
        if(e.status === 401) {
          messages.error(Msg.permissionError)
          auth.logout()
          return
        }
        messages.error(`Errore: ${e.message}`)
      },
    }
  }
})
</script>
